import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

interface Citation {
  uuid: string;
  filename: string;
}

const Citations: React.FC = () => {
  const [citations, setCitations] = useState<Citation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCitations = async () => {
      try {
        const response = await api.get('/api/citation');
        setCitations(response.data.citations || []);
      } catch (err) {
        console.error('Failed to fetch citations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch citations');
      } finally {
        setLoading(false);
      }
    };

    fetchCitations();
  }, []);

  const handleCitationClick = (citation: Citation) => {
    navigate(`/citation/${citation.uuid}/read`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-adalabs-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading citations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Citations</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Citations</h1>
        <p className="text-gray-600">Click on a citation to view its content</p>
      </div>

      {citations.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No citations found</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  UUID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {citations.map((citation) => (
                <tr
                  key={citation.uuid}
                  onClick={() => handleCitationClick(citation)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {citation.filename}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {citation.uuid}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Citations; 